// ===================================================================================================
// PlaceAutocomplete.jsx
// ---------------------------------------------------------------------------------------------------
// Remote search with a smooth UX and proper concurrency control.
// - Debounced keystrokes (150ms).
// - "Take latest" behavior using an increasing request id so only the newest response applies.
// - Stateless BufferService and immutable MSelect integration.
// - Keeps option shape { Id, value, label } to align with MSelect equality checks.
// - Accepts placeTypeId and applies it in the server-side filter (PlaceTypeId eq X).
// ---------------------------------------------------------------------------------------------------

import React, { useEffect, useMemo, useRef, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { BufferService, MSelect } from 'src/M_Components';
import { LoadingContext } from 'src/M_Components';
import { useTranslation } from 'react-i18next';
import { isFailurePayload } from 'src/components/utils/apiResponse';

//====================================================
// Autocomplete for Places backed by server-side paging and filtering.
// Supports debounced query input and "take latest" to avoid racing responses.
//====================================================
export default function PlaceAutocomplete({
  label,
  valueId,
  initialLabel,
  onChange,
  required,
  disabled,
  pageSize = 20,
  placeTypeId, // 1 = Port, 2 = Location
}) {
  const { t } = useTranslation();
  const ctx = useContext(LoadingContext);

  // Create a stable service instance for place queries tied to the current base URL
  const svcPlaces = useMemo(
    () => new BufferService('CargofivePlaces', ctx.DEFAULTURL),
    [ctx.DEFAULTURL]
  );

  // Local option cache for the dropdown menu
  const [options, setOptions] = useState([]); // array of { Id, value, label }
  // Controlled selected value kept in the same shape MSelect expects
  const [selected, setSelected] = useState(null);
  // Used to lazily fetch the first page on focus only once
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // Root wrapper to bind to the underlying input of the MSelect autocomplete
  const wrapperRef = useRef(null);
  // Debounce timer handle
  const debTimer = useRef(null);

  // "Take latest" controls:
  // requestSeq increases on each new fetch; latestApplied stores the latest applied id.
  const requestSeq = useRef(0);
  const latestApplied = useRef(0);

  // Sync selected value from the incoming props id and label
  useEffect(() => {
    if (!valueId) {
      setSelected(null);
      return;
    }
    setSelected({
      Id: Number(valueId),           // keep numeric Id
      value: Number(valueId),        // mirror Id in value for MSelect
      label: initialLabel || `#${valueId}`, // fallback label when only Id is known
    });
  }, [valueId, initialLabel]);

  //====================================================
  // Fetches the first page with an optional search query.
  // Applies "take latest" so only the most recent response wins.
  //====================================================
  const fetchPage1 = async (q) => {
    if (disabled) return; // no-op when disabled

    const myId = ++requestSeq.current; // bump the request id for this call

    try {
      // Sanitize the query for single-quoted OData contains
      const safe = (q || '').trim().replace(/'/g, "''");

      // Build filter clauses from query and placeTypeId
      const clauses = [];
      if (safe) clauses.push(`contains(DisplayName,'${safe}')`);
      if (placeTypeId) clauses.push(`PlaceTypeId eq ${Number(placeTypeId)}`);

      // Encode the filter only when needed
      const filter = clauses.length ? `&Filter=${encodeURIComponent(clauses.join(' and '))}` : '';
      const qs = `?Page=1&PageSize=${pageSize}${filter}`;

      // Fire the request
      const res = await svcPlaces.filter(qs);

      // If a newer request already started, stop here
      if (myId < requestSeq.current) return;

      // Normalize payload, tolerate both paged and array responses
      if (isFailurePayload(res)) throw res;
      const items = Array.isArray(res?.Items) ? res.Items : Array.isArray(res) ? res : [];
      const mapped = items.map((p) => ({
        Id: Number(p?.Id),
        value: Number(p?.Id),
        label: p?.DisplayName || `${p?.Name || ''}${p?.Code ? `, ${p.Code}` : ''}`,
      }));

      // Double-check "take latest" and apply
      if (myId >= latestApplied.current) {
        latestApplied.current = myId;
        setOptions(mapped);
      }
    } catch {
      // On error, clear options only if this call is the latest known
      if (myId >= latestApplied.current) {
        latestApplied.current = myId;
        setOptions([]);
      }
    }
  };

  //====================================================
  // Debounced facade for fetchPage1
  //====================================================
  const debouncedFetch = (q) => {
    if (debTimer.current) clearTimeout(debTimer.current); // clear pending debounce
    debTimer.current = setTimeout(() => fetchPage1(q), 150); // short, snappy debounce
  };

  // Attach to the real input inside MSelect.Autocomplete and react to focus/input
  useEffect(() => {
    if (disabled) return;                         // do nothing when disabled
    const root = wrapperRef.current;
    if (!root) return;

    const input = root.querySelector('input');    // find the inner input
    if (!input) return;

    // On first focus, fetch initial page once
    const onFocus = () => {
      if (!hasLoadedOnce) {
        setHasLoadedOnce(true);
        fetchPage1(''); // prime with the first page
      }
    };

    // On input, trigger debounced fetch with the current text
    const onInput = (e) => {
      const v = e?.target?.value ?? '';
      debouncedFetch(v);
    };

    input.addEventListener('focus', onFocus);
    input.addEventListener('input', onInput);

    // Cleanup listeners on unmount or dependency change
    return () => {
      input.removeEventListener('focus', onFocus);
      input.removeEventListener('input', onInput);
    };
  }, [disabled, hasLoadedOnce, placeTypeId]); // rebind when type changes to refresh behavior

  // When placeTypeId changes, refresh immediately with the current input value
  useEffect(() => {
    if (disabled) return;
    const root = wrapperRef.current;
    const input = root?.querySelector('input');
    const current = input?.value || '';
    fetchPage1(current); // no debounce here to reflect filter change straight away
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeTypeId]);

  return (
    <div ref={wrapperRef}>
      <MSelect
        label={label || t('Place')}
        autocomplete
        required={!!required}
        disabled={!!disabled}
        options={options}                            // array of {Id, value, label}
        value={selected}                             // single selected option in the same shape
        onChangeValue={(sel) => {
          setSelected(sel || null);                  // keep local selection in sync
          onChange?.(sel ? { value: sel.value, label: sel.label } : null); // lift value to parent
        }}
      />
    </div>
  );
}

PlaceAutocomplete.propTypes = {
  label: PropTypes.string,
  valueId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  initialLabel: PropTypes.string,
  onChange: PropTypes.func,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  pageSize: PropTypes.number,
  placeTypeId: PropTypes.number,
};
